import { useCallback, useEffect, useMemo, useRef } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Tooltip,
	Typography,
} from "@mui/material";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import type { PreparedStandRow } from "../models/RollsCartridge";

const gridStyles = (
	<GlobalStyles
		styles={{
			// A stand with nothing prepared is still a stand: only its empty
			// cells are muted, the stand name reads like every other one.
			".rolls-cartridge-grid .rollset-placeholder": {
				color: "#94a3b8",
			},
			".rolls-cartridge-grid .rollset-secondary": {
				backgroundColor: "#f3f4f6 !important",
				color: "#475569 !important",
			},
			// Single selection color, applied on top of whatever the row had
			// underneath (white for primary, grey for secondary). Keeps the
			// "is-selected" signal consistent and unambiguous.
			".rolls-cartridge-grid .rollset-selected": {
				backgroundColor: "#bfdbfe !important",
				color: "#0f172a !important",
			},
			".rolls-cartridge-grid .ag-row-hover:not(.rollset-placeholder)": {
				cursor: "pointer",
			},
			".rolls-cartridge-grid .stand-cell-has-alternates": {
				fontWeight: 700,
				cursor: "pointer",
			},
		}}
	/>
);

interface RollsetsPreparedGridProps {
	rows: PreparedStandRow[];
	selectedRollsetId: number | null;
	onRowClick: (row: PreparedStandRow) => void;
	expandedStands: Set<number>;
	onToggleExpandStand: (standId: number) => void;
	isLoading: boolean;
}

const PLACEHOLDER = "---";

/** Width reserved for the expand control on every stand row, in pixels. */
const CONTROL_SLOT = 18;

interface StandCellContext {
	expandedStands: Set<number>;
	onToggleExpandStand: (standId: number) => void;
}

/**
 * The global AG Grid theme centres every cell's content, which is what pushed
 * the stand names around depending on whether the row had an expand control.
 * Filling the cell leaves centring nothing to distribute, and the names line up
 * on this element's left edge instead.
 */
const StandCellShell = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			width: "100%",
			boxSizing: "border-box",
			pl: 0.75,
			overflow: "hidden",
		}}
	>
		{children}
	</Box>
);

const StandCellRenderer = (
	params: ICellRendererParams<PreparedStandRow> & {
		context?: StandCellContext;
	},
) => {
	const row = params.data;
	const ctx = params.context;
	if (!row) return null;

	const standId = row.PreparedStandId;
	const expanded =
		standId != null && ctx ? ctx.expandedStands.has(standId) : false;

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (standId != null && ctx) {
			ctx.onToggleExpandStand(standId);
		}
	};

	// The expand control keeps its slot on every row, so the names line up
	// whether or not the stand has alternates.
	const control = row.HasAlternates ? (
		expanded ? (
			<ExpandMoreIcon sx={{ fontSize: CONTROL_SLOT, color: "primary.main" }} />
		) : (
			<ChevronRightIcon
				sx={{ fontSize: CONTROL_SLOT, color: "primary.main" }}
			/>
		)
	) : null;

	// An alternate repeats the stand of the row above it. It lines up with the
	// rest and is told apart by the grey, not by an indent.
	if (row.IsSecondary) {
		return (
			<StandCellShell>
				<Box sx={{ width: CONTROL_SLOT, flexShrink: 0 }} />
				<Typography component="span" sx={{ fontSize: 13, color: "inherit" }}>
					{row.StandName}
				</Typography>
			</StandCellShell>
		);
	}

	const name = (
		<Box
			className={row.HasAlternates ? "stand-cell-has-alternates" : undefined}
			onClick={row.HasAlternates ? handleToggle : undefined}
			sx={{ display: "inline-flex", alignItems: "center", minWidth: 0 }}
		>
			<Box
				sx={{
					width: CONTROL_SLOT,
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{control}
			</Box>
			<Typography
				component="span"
				sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}
			>
				{row.StandName}
			</Typography>
			{row.HasAlternates ? (
				<Typography
					component="span"
					sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}
				>
					({row.AlternatesCount})
				</Typography>
			) : null}
		</Box>
	);

	return (
		<StandCellShell>
			{row.HasAlternates ? (
				<Tooltip
					title={`This stand has ${row.AlternatesCount} prepared rollsets. Click to ${
						expanded ? "hide" : "show"
					} the alternates.`}
					arrow
					placement="right"
				>
					{name}
				</Tooltip>
			) : (
				name
			)}
		</StandCellShell>
	);
};

const buildColumnDefs = (): ColDef<PreparedStandRow>[] => [
	{
		field: "StandName",
		headerName: "Stand",
		width: 120,
		pinned: "left",
		cellRenderer: StandCellRenderer,
	},
	{
		field: "Name",
		headerName: "Name",
		width: 130,
		valueFormatter: (p) =>
			p.data?.IsPlaceholder ? PLACEHOLDER : (p.value ?? ""),
	},
	{
		headerName: "Rolls",
		flex: 1,
		minWidth: 220,
		valueGetter: (p) => {
			if (p.data?.IsPlaceholder) return PLACEHOLDER;
			const top = p.data?.TopRollName ?? "";
			const bottom = p.data?.BottomRollName ?? "";
			if (!top && !bottom) return "";
			return `${top}${top && bottom ? " / " : ""}${bottom}`;
		},
	},
	{
		headerName: "Starter Pass",
		width: 150,
		valueGetter: (p) => {
			if (p.data?.IsPlaceholder) return PLACEHOLDER;
			const pos = p.data?.StarterPassPosition;
			const name = p.data?.StarterPassName;
			if (pos == null && !name) return "";
			return `${pos ?? "-"} - ${name ?? "-"}`;
		},
	},
	{
		field: "CartridgeName",
		headerName: "Cartridge",
		width: 130,
		valueFormatter: (p) =>
			p.data?.IsPlaceholder ? PLACEHOLDER : (p.value ?? ""),
	},
	{
		headerName: "Diameters",
		width: 160,
		valueGetter: (p) => {
			if (p.data?.IsPlaceholder) return PLACEHOLDER;
			const top = p.data?.TopRollDiameter;
			const bottom = p.data?.BottomRollDiameter;
			if (top == null && bottom == null) return "";
			if (top != null && bottom != null) {
				return `${top.toFixed(3)} - ${bottom.toFixed(3)}`;
			}
			return (top ?? bottom ?? "").toString();
		},
	},
];

export const RollsetsPreparedGrid = ({
	rows,
	selectedRollsetId,
	onRowClick,
	expandedStands,
	onToggleExpandStand,
	isLoading,
}: RollsetsPreparedGridProps) => {
	const columnDefs = useMemo(() => buildColumnDefs(), []);

	const context = useMemo<StandCellContext>(
		() => ({ expandedStands, onToggleExpandStand }),
		[expandedStands, onToggleExpandStand],
	);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent<PreparedStandRow>) => {
			if (event.data && !event.data.IsPlaceholder) {
				onRowClick(event.data);
			}
		},
		[onRowClick],
	);

	// `rowClassRules` (declarative map) is reactive to prop changes on
	// <AgGridReact>, unlike `getRowClass` which AG Grid caches at first
	// render and does not re-evaluate when external state changes.
	const rowClassRules = useMemo(
		() => ({
			"rollset-placeholder": (params: {
				data?: PreparedStandRow | undefined;
			}) => !!params.data?.IsPlaceholder,
			"rollset-secondary": (params: { data?: PreparedStandRow | undefined }) =>
				!!params.data?.IsSecondary,
			"rollset-selected": (params: { data?: PreparedStandRow | undefined }) =>
				!!params.data &&
				!params.data.IsPlaceholder &&
				selectedRollsetId != null &&
				params.data.Id === selectedRollsetId,
		}),
		[selectedRollsetId],
	);

	// Belt-and-suspenders: force AG Grid to redraw all rows whenever the
	// selection changes, so stale class attributes on the previously-selected
	// row are cleared from the DOM even if rowClassRules cached.
	const gridApiRef = useRef<GridApi<PreparedStandRow> | null>(null);
	const handleGridReady = useCallback(
		(event: GridReadyEvent<PreparedStandRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);
	useEffect(() => {
		gridApiRef.current?.redrawRows();
	}, [selectedRollsetId]);

	return (
		<Box sx={{ position: "relative", height: "100%" }}>
			{gridStyles}
			<Box
				sx={{
					px: 1.5,
					py: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
					Rollsets prepared for line
				</Typography>
			</Box>

			{isLoading && rows.length === 0 && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 1,
						bgcolor: "rgba(255,255,255,0.7)",
					}}
				>
					<CircularProgress />
				</Box>
			)}

			<Box
				className="rolls-cartridge-grid"
				sx={{ height: "calc(100% - 42px)" }}
			>
				<AgGridReact<PreparedStandRow>
					columnDefs={columnDefs}
					rowData={rows}
					context={context}
					rowClassRules={rowClassRules}
					onRowClicked={handleRowClicked}
					onGridReady={handleGridReady}
					defaultColDef={{
						sortable: false,
						resizable: true,
					}}
					suppressContextMenu
					rowHeight={32}
					headerHeight={32}
				/>
			</Box>
		</Box>
	);
};
